// The English catalogue — one template per key of `data/tables/message-keys.json` (ARCH §2.7). The words are
// the requirements' (REQ §7 glossary is binding, D-012; REQ-045 AC1 long forms; REQ-075 hedges; REQ-102 AC4;
// REQ-172 AC1 preset names; REQ-283 / 286 / 287 states) in the DESIGN_SYSTEM §2 voice: plain language, second
// person, contractions, no exclamation marks, what happens to the money.
//
// Template grammar (`render.ts`): `{name}` inserts a param; `{name:modifier}` pins how — a `FormatContext` for
// money / number / pct / date / instant params (`{amount:money.exact}`), a noun form for a result type
// (`plurals` default · `plural` · `singular`), `level` / `name` for an entity, `noun` / `number` for a count.
// The catalogue never formats a digit: every number goes through `@sl/core/format` (REQ-261 AC5), so "$36",
// "38%" and "Wed, Sep 16" are the formatter's, while "20%" in a sentence about the ±20% rule is copy, not a value.

import type { ResultTypeKey } from '../types/entities';
import type { MessageKey } from './keys';
import { RESULT_NOUNS, type ResultNoun } from './resultNouns';

/** The `Intl.PluralRules` categories a noun may need; `other` is the fallback for all of them. */
export interface PluralForms {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

export interface Catalogue {
  /** BCP 47 tag the words are written in (plural rules follow it). */
  locale: string;
  templates: Readonly<Record<MessageKey, string>>;
  /** Nouns for a `count` param's `unit` (besides result types, which come from `resultNouns`). */
  units: Readonly<Record<string, PluralForms>>;
  resultNouns: Readonly<Record<ResultTypeKey, ResultNoun>>;
}

export const en: Catalogue = {
  locale: 'en',
  units: {
    campaign: { one: 'campaign', other: 'campaigns' },
    ad_set: { one: 'ad set', other: 'ad sets' },
    ad: { one: 'ad', other: 'ads' },
    entity: { one: 'item', other: 'items' },
    change: { one: 'change', other: 'changes' },
    alert: { one: 'alert', other: 'alerts' },
    result: { one: 'result', other: 'results' },
    day: { one: 'day', other: 'days' },
    hour: { one: 'h', other: 'h' },
    minute: { one: 'min', other: 'min' },
    time: { one: 'time', other: 'times' },
  },
  resultNouns: RESULT_NOUNS,
  templates: {
    // ---- status (REQ-045 AC1 short label · long form · AC2 what to do) ---------------------------------
    'status.running.short': 'Running',
    'status.running.long': 'Running',
    'status.running.what_to_do': 'Nothing to do — keep an eye on its cost per result.',
    'status.learning.short': 'Learning',
    'status.learning.long': 'Learning — give it time',
    'status.learning.what_to_do':
      'Leave its budget and audience alone until it settles — a big change starts the learning again.',
    'status.learning_limited.short': 'Learning limited',
    'status.learning_limited.long': 'Learning limited — not enough results',
    'status.learning_limited.what_to_do':
      'It still runs and spends. A bigger budget, a wider audience or a simpler result to aim for can restart the learning.',
    'status.off_by_you.short': 'Off',
    'status.off_by_you.long': 'Off — you turned it off',
    'status.off_by_you.what_to_do': 'Turn it on when you want it to run again.',
    'status.off_campaign.short': 'Off',
    'status.off_campaign.long': 'Off — its campaign is off',
    'status.off_campaign.what_to_do': 'Turn the campaign on to run everything inside it.',
    'status.off_adset.short': 'Off',
    'status.off_adset.long': 'Off — its ad set is off',
    'status.off_adset.what_to_do': 'Turn the ad set on to run the ads inside it.',
    'status.scheduled.short': 'Scheduled',
    'status.scheduled.long': 'Scheduled — starts {date}',
    'status.scheduled.what_to_do': 'Nothing to do, unless the date is wrong.',
    'status.ended.short': 'Ended',
    'status.ended.long': 'Ended {date}',
    'status.ended.what_to_do': 'Give it a later end date if you want it to run again.',
    'status.in_review.short': 'In review',
    'status.in_review.long': 'In review — Meta is checking this ad',
    'status.in_review.what_to_do': 'Nothing to do — it starts on its own once approved.',
    'status.rejected.short': 'Rejected',
    'status.rejected.long': 'Not running — Meta rejected this ad',
    'status.rejected.what_to_do':
      "Read Meta's reason on the ad's page, then fix the ad or appeal in Ads Manager.",
    'status.has_problem.short': 'Has a problem',
    'status.has_problem.long': 'Has a problem — {reason}',
    'status.has_problem.what_to_do':
      'The reason above says what to fix; the fix usually lives in Ads Manager.',
    'status.account_problem.short': 'Account problem',
    'status.account_problem.long': 'Account problem — nothing can run',
    'status.account_problem.what_to_do':
      'Fix it in Ads Manager under Billing or Account quality; everything starts again on its own afterwards.',
    'status.not_spending.short': 'Not spending',
    'status.not_spending.long': "Not spending — it's on, but barely delivering",
    'status.not_spending.what_to_do':
      'Common causes: a very small audience, a budget or bid too low, or a worn-out ad. A wider audience or a fresh ad usually helps.',

    // ---- issue (the plain reason behind a status) ------------------------------------------------------
    'issue.removed': 'removed in Ads Manager',
    'issue.nothing_running_inside': 'nothing inside it is running',
    'issue.unknown_status': "Meta reported a status we don't recognise yet",
    'issue.campaign_off': 'its campaign is off',
    'issue.adset_off': 'its ad set is off',
    'issue.campaign_scheduled': 'its campaign starts {date}',
    'issue.adset_scheduled': 'its ad set starts {date}',
    'issue.campaign_ended': 'its campaign has ended',
    'issue.adset_ended': 'its ad set has ended',
    'issue.budget_used_up': 'its total budget is used up',
    'issue.spent_little_yesterday': 'spent {spent:money.exact} of {budget:money.perDay} yesterday',
    'issue.meta_summary': '{summary}',
    'issue.rejected_policy': "Meta's review found it against its advertising rules",
    'issue.rejected_feedback': "Meta's review says: {feedback}",
    // D-393 — the one clause a held problem adds to an Off long form ("Off — its campaign is off. Meta also …")
    'issue.held_rejected': "Meta also rejected it — it won't run when turned on until that's fixed",
    'issue.held_in_review': 'Meta is also still reviewing it',
    'issue.held_problem': 'Meta also reports a problem with it: {reason}',
    'issue.held_problem_unnamed': 'Meta also reports a problem with it',
    'issue.payment_failed': "a payment didn't go through",
    'issue.spending_limit_reached': "the account's spending limit has been reached",
    'issue.page_unpublished': 'its Facebook Page is unpublished',
    'issue.page_restricted': 'its Facebook Page is restricted from advertising',
    'issue.instagram_account_problem': 'its Instagram account has a problem Meta wants fixed',
    'issue.pixel_inactive': "its pixel hasn't sent any events lately, so Meta can't count results",
    'issue.audience_too_small': 'its audience is too small to deliver',
    'issue.link_broken': "its website link doesn't work",
    'issue.budget_too_low': "its budget is below Meta's minimum",
    'issue.special_category_required':
      'Meta needs a special ad category declared for it (housing, credit, employment or social issues)',
    'issue.creative_missing': 'its image or video is missing',
    'issue.account_disabled': 'Meta disabled the ad account',
    'issue.account_unsettled': 'the ad account has an unpaid balance',
    'issue.account_pending_review': 'Meta is reviewing the ad account',
    'issue.account_closed': 'the ad account is closed',
    'issue.account_grace_period':
      'a payment is overdue — your ads keep running for now, but not for long',

    // ---- efficiency (REQ-073 AC1 — one fixed scale, decided by the server) -----------------------------
    'efficiency.much_cheaper.label': 'Much cheaper',
    'efficiency.cheaper.label': 'Cheaper',
    'efficiency.about_average.label': 'About average',
    'efficiency.more_expensive.label': 'More expensive',
    'efficiency.much_more_expensive.label': 'Much more expensive',
    'efficiency.no_results.label': 'Spent with no results',
    'efficiency.not_enough_data.label': 'Not enough data',
    'efficiency.much_cheaper.statement': '{pct:pct.share} cheaper than your account average',
    'efficiency.cheaper.statement': '{pct:pct.share} cheaper than your account average',
    'efficiency.about_average.statement': 'About average for your account',
    'efficiency.more_expensive.statement':
      '{pct:pct.share} more expensive than your account average',
    'efficiency.much_more_expensive.statement':
      '{pct:pct.share} more expensive than your account average',
    'efficiency.no_results.statement': 'Spent {spent:money.exact} with no results',
    'efficiency.not_enough_data.statement': 'Not enough data to compare yet',
    'efficiency.learning_too_early': 'Learning — too early to tell',
    'efficiency.nothing_to_compare': 'Nothing to compare with',
    'efficiency.count_unavailable': 'Not available for these dates',
    'efficiency.legend.same_result':
      "Compared with your account's cost per result for the same result type",

    // ---- share (REQ-073 AC3, REQ-074 AC1–AC4 — every share names its denominator) -------------------------
    'share.of_account_spend': '{share:pct.share} of your account spend',
    'share.of_parent_spend': "{share:pct.share} of the {parent:level}'s spend",
    'share.of_type_spend':
      '{share:pct.share} of spend among {peers:noun} that aim for {result:plurals}',
    'share.of_type_results':
      '{share:pct.share} of the {result:plurals} from {peers:noun} that aim for them',
    'share.of_type_value':
      '{share:pct.share} of the value from {peers:noun} that aim for {result:plurals}',
    'share.label.costing_more': 'Costing more than it brings in',
    'share.label.pulling_more': 'Pulling more than its weight',
    'share.label.in_line': 'In line with its results',
    'share.gap.sentence':
      'It takes {spendShare:pct.share} of the spend and brings in {resultsShare:pct.share} of the {result:plurals}',

    // ---- wasted (REQ-075 — always hedged) ----------------------------------------------------------------
    'wasted.headline':
      'Possibly wasted: {amount:money.exact} of {total:money.headline} spent ({share:pct.share})',
    'wasted.rule.no_results': 'No results',
    'wasted.rule.too_expensive': 'Far too expensive',
    'wasted.rule.worn_out': 'Worn out',
    'wasted.no_results.reason':
      'Possibly wasted — spent {spent:money.exact} with no {result:plurals}',
    'wasted.too_expensive.reason':
      'Possibly wasted — {cost:money.cost} per {result:singular} looks far too expensive next to the {median:money.cost} typical at this level',
    'wasted.worn_out.reason':
      'Possibly wasted · looks worn out — seen {seen:ratio.frequency} times on average, click rate down {clickDrop:pct.share} since its first week',
    'wasted.learning_exempt':
      'Not checked while learning — an ad set that has been learning for under 7 days is never flagged as possibly wasted',
    'wasted.absorbed': 'Counted inside {entity}',
    'wasted.heading': "Explained by what's inside it",
    'wasted.none': 'Nothing looks wasted in this period.',
    'wasted.dismissed': 'Marked as fine until {until:date.short}',

    // REQ-071 AC3–4 / REQ-076 AC4 — the "75%" is the constant's, rendered; the money is the formatter's.
    'budget.marked_day':
      'Meta may spend up to {flexPct:pct.share} more than your daily budget on a given day and evens it out over the week',
    'budget.over_meta_limit.week':
      'It spent {spent:money.exact} in the week of {since:date.day} – {until:date.day} — more than the {allowed:money.exact} its daily budget allows for that week',
    'budget.over_meta_limit.day':
      'It spent {spend:money.exact} on {date:date.day} — more than {flexPct:pct.share} above its {budget:money.perDay} daily budget',
    'budget.allowed_per_day':
      "You've allowed up to {dailyBudgets:money.headline} per day in daily budgets",
    'budget.allowed_per_day.with_total':
      "You've allowed up to {dailyBudgets:money.headline} per day in daily budgets, plus about {totalBudgets:money.headline} per day from total budgets",

    // ---- pacing (REQ-071 AC1, REQ-024 AC1 / AC4, ENGINE §7.1) ---------------------------------------------
    // The amount is always POSITIVE — the direction is the key's word, never a minus sign in front of money.
    'pacing.label.on_track': 'On track',
    'pacing.label.under_plan': 'Under plan',
    'pacing.label.over_plan': 'Over plan',
    'pacing.label.too_early': 'Too early to tell',
    'pacing.label.no_plan': 'No monthly plan',
    'pacing.over_plan': 'Over plan · {ahead:money.headline} ahead',
    'pacing.under_plan': 'Under plan · {behind:money.headline} behind',
    'pacing.on_track.ahead': 'On track · {ahead:money.headline} ahead',
    'pacing.on_track.behind': 'On track · {behind:money.headline} behind',
    'pacing.on_track.on_plan': 'On track · exactly on plan',
    'pacing.too_early': 'Too early to tell — the month has only just started',
    'pacing.no_plan': 'No monthly plan set',

    // ---- forecast (REQ-072 AC1 / AC4, REQ-024 AC2, REQ-301 AC2, ENGINE §7.2–7.3) --------------------------
    'forecast.range':
      'On course to spend {low:money.headline}–{high:money.headline} by {until:date.short}',
    'forecast.vs_plan.over': 'about {amount:money.headline} over your {plan:money.headline} plan',
    'forecast.vs_plan.under': 'about {amount:money.headline} under your {plan:money.headline} plan',
    'forecast.vs_plan.on_plan': 'about the same as your {plan:money.headline} plan',
    'forecast.not_enough_history': 'Not enough history to forecast yet',
    'forecast.guardrail_hit':
      'On course to reach your {guardrail:money.headline} guardrail on {date:date.day}',

    // ---- verdict (REQ-040 AC2, ENGINE §16) ----------------------------------------------------------------
    'verdict.needs_attention.label': 'Needs attention',
    'verdict.keep_an_eye.label': 'Keep an eye on it',
    'verdict.going_well.label': 'Going well',
    'verdict.too_early.label': 'Too early to tell',
    'verdict.none.not_running': "No verdict — it isn't running",
    'verdict.none.too_early': 'Too early to tell — still learning, or too little spent to judge',
    'verdict.none.in_band': 'About average — nothing to flag',
    'verdict.none.count_unavailable': "No verdict — Meta hasn't sent how many people it reached",
    'verdict.reason.possibly_wasted':
      'possibly wasted: {spent:money.exact} with no {result:plurals}',
    'verdict.reason.possibly_wasted_too_expensive':
      'possibly wasted: {cost:money.cost} per {result:singular} looks far too expensive',
    'verdict.reason.possibly_wasted_worn_out': 'possibly wasted: looks worn out',
    'verdict.reason.possibly_wasted_inside': 'possibly wasted inside it: {entity}',
    'verdict.reason.rejected': 'Meta rejected this ad',
    'verdict.reason.rejected_inside': 'Meta rejected an ad inside it: {entity}',
    'verdict.reason.has_problem': 'has a problem',
    'verdict.reason.problem_inside': '{entity} has a problem',
    'verdict.reason.not_spending': "not spending — it's on, but barely delivering",
    'verdict.reason.not_spending_inside': '{entity} is barely delivering',
    'verdict.reason.open_alert': 'has an open Heads up alert',
    'verdict.reason.open_alert_inside': '{entity} has an open Heads up alert',
    'verdict.reason.above_account': '{cost:money.cost} — {pct:pct.share} above the account average',
    'verdict.reason.learning_limited': 'learning limited — not enough results',
    'verdict.reason.not_using_budget': 'using only {pct:pct.share} of its budget',
    'verdict.reason.going_well':
      '{cost:money.cost} per {result:singular} — at or below the account average, with {results}',

    // ---- warning (ENGINE §11 change warnings, then the seven API warnings) -----------------------------
    'warning.learning_restart_likely':
      "Changes this big ({changePct:pct.share}) usually restart Meta's learning — results may wobble for a few days. Consider ±20% steps.",
    'warning.learning_restarts':
      "It's been off for {offFor} — turning it on restarts Meta's learning, so results may wobble for a few days.",
    'warning.plan_exceeded':
      'With this change the month is heading for ≈ {forecast:money.headline} — over your {plan:money.perDay} plan',
    'warning.guardrail_exceeded':
      'With this change the month is heading for ≈ {forecast:money.headline} — over your {guardrail:money.perDay} guardrail',
    'warning.last_running_ad':
      'This is the last running ad in {entity} — the ad set will stop delivering',
    'warning.wont_deliver_parent_off': "It still won't deliver: {parent} is off. Turn that on too.",
    'warning.wont_deliver_ended':
      "It still won't deliver: it ended {date:date.day}. Give it a later end date first.",
    'warning.wont_deliver_rejected':
      "It still won't deliver: Meta rejected this ad. Fix it or appeal in Ads Manager first.",
    'warning.below_recommended_budget':
      "Below Meta's recommended budget of {min:money.perDay} per day — it may deliver less than expected",
    'warning.stale_data': "These numbers are from {asOf} — we haven't been able to refresh since",
    'warning.rate_limited':
      'Meta is limiting how often we can check — next update at {nextAttemptAt}',
    // D-396 — a capped list says so in one sentence about one population; the title names rows, not numbers
    'warning.partial_data':
      'Every campaign and ad set is here. {levels} are capped at the {shown} that spent the most in this period; the {leftOut} left out spent {leftOutSpend:money.exact} between them. Search finds any {level} by name.',
    'warning.partial_data_nothing':
      'Every campaign and ad set is here. {levels} are capped at the {shown} that spent the most in this period; the {leftOut} left out spent nothing between them. Search finds any {level} by name.',
    'warning.partial_data_list':
      '{levels} are capped at the {shown} that spent the most in this period; the {leftOut} left out spent {leftOutSpend:money.exact} between them. Search finds any {level} by name.',
    'warning.partial_data_list_nothing':
      '{levels} are capped at the {shown} that spent the most in this period; the {leftOut} left out spent nothing between them. Search finds any {level} by name.',
    'warning.truncated_title': 'Not every {level} is listed',
    // T-290 (D-398, D-416) — the one row sentence both apps held as a literal (`TREE_COPY.adsNotLoaded`): ad sets only,
    // where the pull pruned ads that are off and delivered nothing in the window; the count is real, and so is the reason
    'row.ads_not_loaded': '{count} · none ran in this period',
    'warning.connection_problem':
      "We couldn't reach your ad account for the latest numbers — showing what we have",
    'warning.reach_unavailable':
      "People reached isn't available for this range — Meta keeps it for 13 months, one period at a time",
    'warning.history_limited': "History goes back {days} on this plan — earlier days aren't shown",
    'warning.granularity_reduced':
      "Hourly figures aren't available yet, so this shows the last 7 days by day.",

    // ---- validation (REQ-102 AC3 — messages say what the limit is) -------------------------------------
    'validation.budget.not_a_number': 'Enter an amount, like 25 or 12.50',
    'validation.budget.not_positive': 'The budget has to be more than zero',
    'validation.budget.below_minimum': 'The minimum here is {min:money.perDay}',
    'validation.budget.below_spent':
      "A total budget can't be below what's already been spent ({spent:money.exact})",
    'validation.budget.edit_campaign_budget_instead':
      "This ad set shares its campaign's budget — edit the campaign's budget instead",
    'validation.period.invalid_range':
      'Pick a start date on or before the end date, and an end date no later than today',
    'validation.period.before_history':
      'History goes back {days} on this plan — pick a start date within that',
    'validation.entities.unknown_level': 'Pick a level: campaigns, ad sets or ads',
    'validation.entities.parent_level':
      'That parent cannot hold this level — ad sets belong to a campaign, ads to a campaign or an ad set',
    'validation.plan.month': 'Pick a month within the last {days} — earlier months are not kept',
    'validation.plan.not_positive': 'The amount has to be more than zero',
    'validation.plan.guardrail_below_plan':
      'The guardrail is a ceiling, so it has to be at least your {plan:money.headline} plan',
    'validation.plan.campaign_over_account':
      'Your campaign plans add up to {total:money.headline} — more than the {plan:money.headline} you set for the account',
    'validation.allocation.basis': 'Choose what to compare by: results or their value',
    'validation.settings.attribution':
      'Pick one of the three ways Meta can count results: within 1 day of a click, within 7 days of a click, or within 7 days of a click or 1 day of a view',
    'validation.settings.override_campaign': 'That campaign is not in this ad account',
    'validation.settings.override_type':
      'Nothing like that happened in {entity} in the last 90 days — pick something it actually got',
    'validation.settings.primary_result':
      'Pick a result your account actually has — this one has none',
    'validation.preview.placement': 'Pick a place the ad can appear: a feed, stories or reels',
    'validation.preview.not_available': 'This ad cannot run in that place',
    'validation.rule.invalid':
      "This rule can't be saved as it is — pick a metric, a condition with a value above zero, and a window",
    'validation.rule.system': "Built-in alerts are always on and can't be edited or deleted",
    // T-167, API §9.4: a link action stages nothing; a moved value under an action (`?demoFault=act:conflict`); the snooze picker
    'validation.alert.action_navigates':
      'This suggestion opens a screen rather than staging a change — follow it from the alert',
    'validation.alert.value_moved':
      'This budget is {current:money.perDay} per day now — it changed since the alert suggested {proposed:money.perDay}. Have another look before staging it',
    'validation.alert.snooze_duration':
      'Pick how long to snooze this: 1 day, 3 days, 1 week, or until you turn it back on',
    'validation.alert.too_many_entities':
      'Choose at most {max:number} campaigns, ad sets or ads for one alert',

    // ---- alert (REQ-172 presets · custom rules · fired sentences · classes) ------------------------------
    'alert.class.good_news': 'Good news',
    'alert.class.heads_up': 'Heads up',
    'alert.class.heads_up_urgent': 'Heads up · urgent',
    'alert.preset.1.title': 'Cost per result jumped',
    'alert.preset.2.title': 'Spending with nothing to show',
    'alert.preset.3.title': 'On course to overspend the month',
    'alert.preset.4.title': 'Something stopped running',
    'alert.preset.5.title': 'An ad is getting tired',
    'alert.preset.6.title': 'Results getting cheaper',
    'alert.preset.7.title': 'A winner is held back by budget',
    'alert.preset.8.title': 'Best day in 30 days',
    'alert.preset.1.sentence':
      'Cost per result at least {pct:pct.share} above its 14-day level over {window}',
    'alert.preset.2.sentence': 'Spent {amount:money.perDay} or more with no results over {window}',
    'alert.preset.3.sentence':
      'Forecast more than {pct:pct.share} over your {plan:money.perDay} monthly plan',
    'alert.preset.4.sentence': 'Spent nothing yesterday, or Meta rejected it or reported a problem',
    'alert.preset.5.sentence':
      'Average times seen above {seen:ratio.frequency} and click rate down {drop:pct.share} or more since its first 7 days',
    'alert.preset.6.sentence':
      'Cost per result at least {pct:pct.share} below its 14-day level over {window}',
    'alert.preset.7.sentence':
      'Cost per result at least {betterBy:pct.share} better than your account average, a daily budget at least {budgetUse:pct.share} used, and at least {minResults} over {window}',
    'alert.preset.8.sentence': 'A new best day for {result:plurals} in the last 30 days',
    'alert.unavailable.no_plan':
      'Set a monthly plan first — this alert compares your forecast with it',
    'alert.custom.above': 'Tell me when {metric} for {scope} is above {limit} over {window}',
    'alert.custom.below': 'Tell me when {metric} for {scope} is below {limit} over {window}',
    'alert.custom.up_by':
      'Tell me when {metric} for {scope} goes up by {pct:pct.share} or more over {window}, compared with the period before',
    'alert.custom.down_by':
      'Tell me when {metric} for {scope} goes down by {pct:pct.share} or more over {window}, compared with the period before',
    'alert.fired.cost_jumped':
      '{entity}: cost per {result:singular} is {cost:money.cost} — {pct:pct.share} above its 14-day level of {level:money.cost}',
    'alert.fired.no_results':
      '{entity} spent {spent:money.headline} in {window} with no {result:plurals}',
    'alert.fired.overspend':
      'This month is heading for ≈ {forecast:money.headline} — about {pct:pct.share} over your {plan:money.perDay} plan',
    'alert.fired.overspend_near_guardrail':
      'This month is heading for ≈ {forecast:money.headline} — about {pct:pct.share} over your {plan:money.perDay} plan, and close to your {guardrail:money.perDay} guardrail',
    'alert.fired.guardrail':
      'This month is heading for ≈ {forecast:money.headline} — past your {guardrail:money.perDay} guardrail',
    'alert.fired.stopped_running': '{entity} stopped running — it spent nothing yesterday',
    'alert.fired.rejected': 'Meta rejected an ad: {entity}',
    'alert.fired.has_problem': "{entity} has a problem and isn't running properly",
    'alert.fired.cheaper':
      '{entity}: cost per {result:singular} is {cost:money.cost} — {pct:pct.share} below its 14-day level of {level:money.cost}',
    'alert.fired.winner_limited':
      '{entity} is a winner held back by its budget: {cost:money.cost} per {result:singular} — {betterBy:pct.share} better than your account average — and using {budgetUse:pct.share} of its budget',
    'alert.fired.best_day':
      '{date:date.day} was your best day in 30 days: {results} (previous best {previous:count})',
    'alert.fired.best_day_value':
      '{date:date.day} was your best day in 30 days: {value:money.cost} in value of results (previous best {previous:money.cost})',
    'alert.fired.custom_above':
      '{entity}: {metric} is {value} over {window} — above your {limit} limit',
    'alert.fired.custom_below':
      '{entity}: {metric} is {value} over {window} — below your {limit} limit',
    'alert.fired.custom_up_by':
      '{entity}: {metric} is up {pct:pct.share} over {window}, compared with the period before',
    'alert.fired.custom_down_by':
      '{entity}: {metric} is down {pct:pct.share} over {window}, compared with the period before',
    'alert.fired.grouped': '{count}: {title}',
    'alert.fired.also.cheaper':
      'Also: cost per {result:singular} is {pct:pct.share} below its 14-day level',
    'alert.fired.also.cost_jumped':
      'Also: cost per {result:singular} is {pct:pct.share} above its 14-day level',
    'alert.fired.also.no_results': 'Also: spent {spent:money.headline} with no {result:plurals}',
    'alert.fired.also.rule': 'also: {title}',
    // T-166 — the two built-in rules as the rules list names them, the scope words of a custom rule's sentence,
    // and the auto-generated name of a custom rule (REQ-170 AC5). Open to a designer / PM read like D-255's.
    'alert.system.guardrail.title': 'Monthly guardrail',
    'alert.system.guardrail.sentence':
      "This month's spend reaches {warnAt:pct.share} or {reachedAt:pct.share} of your guardrail, or the forecast passes it",
    'alert.system.over_meta_limit.title': 'More than Meta should spend',
    'alert.system.over_meta_limit.sentence':
      "A campaign or ad set spends more per day or per week than Meta's own budget flexibility allows",
    'alert.scope.account': 'the account',
    'alert.scope.any_campaign': 'any running campaign',
    'alert.scope.any_adset': 'any running ad set',
    'alert.scope.any_ad': 'any running ad',
    'alert.scope.any_budget_owner': 'any running ad set or campaign with its own budget',
    'alert.scope.any_entity': 'any running campaign, ad set or ad',
    'alert.scope.entities': 'the {count} you chose', // D-484 (T-290): the article, for the picker T-293 / T-294
    // D-395 — the inbox's lead sentence and empty states (REQ-173 AC7); built-in rules are not counted
    'alert.lead.none':
      "You haven't switched on any rules yet. Pick a ready-made one and good news and heads-ups land here.",
    'alert.lead.one': 'Good news and heads-ups from the one rule watching this account.',
    'alert.lead.many': 'Good news and heads-ups from the {rules} rules watching this account.',
    'alert.empty.no_rules.title': 'Nothing is watching your results yet',
    'alert.empty.no_rules.body':
      "Switch on a ready-made rule and we'll tell you when a cost jumps, something stops running, or a day sets a record.",
    'alert.empty.no_rules.action': 'Choose a rule',
    'alert.empty.caught_up.title': 'All caught up',
    'alert.empty.caught_up.body':
      'Nothing needs you right now. Your rules are still watching this account.',
    // REQ-170 AC4 v1.6 — why noise control held entities back at the back-test's last evaluation (ENGINE §10.3)
    'alert.backtest.guard.min_spend': '{count} spent too little in the window to be judged',
    'alert.backtest.guard.min_impressions': '{count} shown too few times to be judged',
    'alert.backtest.guard.min_results': '{count} had too few results for a cost per result',
    'alert.backtest.guard.count_unavailable': "{count} without a count Meta hasn't sent",
    'alert.backtest.guard.no_denominator': '{count} with nothing to compare against',
    'alert.backtest.guard.not_available': '{count} without this figure',
    'alert.backtest.guard.learning': '{count} still learning, so not judged yet',
    'alert.name.custom_above': '{metric} above {limit}',
    'alert.name.custom_below': '{metric} below {limit}',
    'alert.name.custom_up_by': '{metric} up {pct:pct.share} or more',
    'alert.name.custom_down_by': '{metric} down {pct:pct.share} or more',
    'alert.name.custom_metric': '{metric}',

    // ---- action (REQ-175 — concrete labels, verbs first) -------------------------------------------------
    'action.raise_to': 'Raise to {amount:money.perDay} per day',
    'action.lower_to': 'Lower to {amount:money.perDay} per day',
    'action.turn_off': 'Turn off this {entity:level}',
    'action.turn_on': 'Turn on this {entity:level}',
    'action.see_why': 'See why',
    'action.see_where_to_trim': 'See where to trim',
    'action.set_plan': 'Set a monthly plan',
    'action.edit_campaign_budget': "Edit the campaign's budget",

    // ---- change (REQ-103 AC2 consequences — always say what happens to the money) ----------------------
    'change.turn_off':
      'Turns off {adsets} and {ads} · about {perDay:money.headline} per day stops spending',
    'change.turn_off_ads': 'Turns off {ads} · about {perDay:money.headline} per day stops spending',
    'change.turn_off_one': 'Turns off · about {perDay:money.headline} per day stops spending',
    'change.turn_off_shifts':
      "its {owner:level}'s {perDay:money.perDay} per day shifts to the other {siblings:noun} — no change in spend",
    'change.turn_on': 'Turns on — can spend up to {perDay:money.perDay} per day',
    'change.turn_on_total': 'Turns on — can spend up to its {total:money.perDay} total budget',
    'change.budget':
      '{from:money.perDay} → {to:money.perDay} per day · about {deltaPerDay:money.headline} per day',
    'change.staged': 'Staged · about {deltaPerDay:money.headline} per day',
    // T-311 (0.3.12) — REQ-102 AC7 / D-710: the impact preview's results half, server-written. The two hedges are
    // load-bearing and fixed ("Could mean roughly" · "if performance holds"); a range of WHOLE results in the
    // result's own noun, never a single number, a percentage or a value figure; the basis sentence is required
    // with it; the `short` form is the designed no-answer and names the figure that is short (ENGINE §11).
    'change.outlook.more':
      'Could mean roughly {low}–{high:number} more {high:noun} a week, if performance holds.',
    'change.outlook.fewer':
      'Could mean roughly {low}–{high:number} fewer {high:noun} a week, if performance holds.',
    'change.outlook.about_more':
      'Could mean about {count:number} more {count:noun} a week, if performance holds.',
    'change.outlook.about_fewer':
      'Could mean about {count:number} fewer {count:noun} a week, if performance holds.',
    'change.outlook.basis':
      'Based on this {entity:level}’s last {days} at {cost:money.cost} per {result:singular}.',
    'change.outlook.short':
      'We can’t say what this would do to results yet — this {entity:level} has had {count} in the last {days}.',

    // ---- error (REQ-287 AC3, DESIGN_SYSTEM §2 rule 6: what happened · ads / spend · what next) ---------
    'error.validation_failed':
      "Something in that request wasn't right. Nothing was changed. Check it and try again.",
    'error.not_found':
      "We couldn't find that. It may have been removed. Your ads and spend are not affected.",
    'error.publish_disabled': 'Publishing is off for this account. Nothing was sent to Meta.',
    'error.not_demo': 'That only works on the demo account. Nothing was changed.',
    'error.change_blocked':
      'One of the selected changes has a problem, so nothing was published. Fix it or leave it out, then publish again.',
    'error.conflict_stale_change':
      'Something changed in Ads Manager since you staged this. Nothing was published. Review the change and confirm it.',
    'error.undo_expired': "It's too late to undo that. The change stays as published.",
    'error.refresh_too_soon': 'You refreshed a moment ago. Try again in a few minutes.',
    'error.rate_limited':
      'Meta is slowing us down for a moment. Your ads keep running; the numbers here will catch up shortly.',
    'error.connection_failed':
      "We couldn't reach your ad account. Your ads keep running; check the connection in Settings.",
    'error.not_ready':
      'Your numbers are still loading for the first time. This usually takes a minute.',
    // REQ-002 AC2 v2.9 (T-381 (j), D-1127 (j)) — the same 409 with NO first sync running. API §2.3's T-204
    // note makes that a LASTING state on a live account rather than a moment, so neither "this usually takes
    // a minute" (a promise the product cannot keep) nor "Nothing to show here yet" (false of a full account)
    // is true of it. No server sends this key: the client picks it over `error.not_ready` from the first-sync
    // fact it has already resolved.
    'error.not_ready_live':
      'Your account is connected; these numbers haven’t arrived yet. We’ll show them as soon as they do.',
    'error.plan_limit_reached':
      "You've reached the limit of your plan for this. Nothing was changed.",
    'error.internal_error':
      'Something went wrong on our side. Your ads and spend are not affected. Try again in a moment.',
    'error.network_error':
      "We couldn't reach the server. Check your connection and try again. Your ads keep running.",
    'error.timeout':
      'That took too long and was stopped. Your ads keep running. Try again in a moment.',
    // T-364 (D-892): a period-keyed answer that carries no period is malformed, not slow — the surface says so and
    // offers the retry, never a skeleton that cannot resolve.
    'error.malformed_answer':
      "The answer we got back was incomplete, so we didn't show it. Your ads keep running. Try again in a moment.",
    'error.unknown':
      "Something didn't work. Your ads and spend are not affected. Try again in a moment.",

    // ---- state (REQ-287 designed states, REQ-283 freshness, connection reasons) -------------------------
    'state.loading': 'Loading…',
    'state.slow': 'This is taking longer than usual',
    'state.empty': 'Nothing to show here yet',
    /** REQ-002 AC2 v2.9 — the heading over `error.not_ready_live`, where a live account LIVES. */
    'state.not_ready.title': 'Your numbers aren’t ready yet',
    'state.not_enough_data': 'Not enough data yet',
    'state.error': 'Something went wrong loading this',
    'state.refreshing': 'Refreshing…',
    'state.updated': 'Updated {ago}',
    'state.may_be_out_of_date': 'may be out of date',
    'state.sample_data': 'Sample data',
    'state.today.so_far': 'so far — still coming in',
    'state.today.label': 'Today — so far, still coming in',
    'state.partial': 'Some numbers are missing — showing what we have',
    'state.rate_limited': 'Limited by Meta — next update at {nextAttemptAt}',
    'state.rate_limited_stale':
      'Limited by Meta — these numbers are from {asOf}; next update at {nextAttemptAt}',
    'state.rate_limited_since': 'Limited by Meta — these numbers are from {asOf}',
    'state.offline': "You're offline — showing what we last saved",
    'state.offline_since': "You're offline — these numbers are from {asOf}",
    'state.first_sync':
      'Loading your account for the first time — this can take a few minutes. You can use what has already arrived.',
    'state.refresh_wait': 'Next refresh in {wait}',
    'state.connection.syncing_first_time': 'Loading your account for the first time',
    'state.connection.token_invalid':
      'Your Facebook connection has expired — reconnect to keep the numbers fresh. Your ads keep running.',
    'state.connection.permission_missing':
      "AdOrchestrator doesn't have permission to read this ad account — reconnect and allow access. Your ads keep running.",
    'state.connection.rate_limited':
      'Meta is limiting how often we can check — showing what we have',
    'state.connection.unreachable':
      "We can't reach Meta right now — showing what we have. Your ads keep running.",
    'state.connection.account_problem':
      "There's a problem with the ad account itself — fix it in Ads Manager under Billing & payments or Account quality",

    // ---- activity (API §7 — `ActivityEntry.summary`; T-027 loose end (3), T-237) -----------------------
    //
    // One rendered sentence per log row, in the first person because the log is the user's own history
    // (REQ-105). The seed's three rows are `turned_off`, `budget_raised` and `plan_changed`; the two
    // inverses are here so the log can describe a publish in either direction rather than growing a key
    // the day someone turns an ad back on.
    'activity.turned_on': 'You turned on {entity:level} {entity}',
    'activity.turned_off': 'You turned off {entity:level} {entity}',
    // D-193 (2) — **"daily", because "budget" alone is the one word this product refuses to leave
    // ambiguous.** REQ-076 makes daily-vs-total the whole point of the budget surfaces and this account
    // holds both kinds three lines apart in the same log. Safe as well as clearer: all four of these keys
    // already format their amounts with `money.perDay`, so none of them can ever carry a total budget —
    // that row would need a key of its own whatever this one is called. (`activity.turned_off` does NOT
    // move: D-193 (1) keeps entity names unquoted in product sentences, quoted only in plain-text exports.)
    'activity.budget_raised':
      "You raised {entity}'s daily budget {before:money.perDay} → {after:money.perDay}",
    'activity.budget_lowered':
      "You lowered {entity}'s daily budget {before:money.perDay} → {after:money.perDay}",
    'activity.plan_changed':
      'You set the monthly plan to {plan:money.compact} and the guardrail to {guardrail:money.compact}',
    'activity.wasted_dismissed':
      'You said {entity:level} {entity} is fine — not flagged again until {until:date.short}',
    'activity.settings.counting': 'You changed how results are counted to "{value}"',
    'activity.settings.result_override': 'You set {entity} to count {resultType} as its result',
    'activity.settings.analysis_excluded': 'You excluded {entity} from analysis and averages',
    'activity.settings.analysis_included': 'You included {entity} in analysis and averages',
    'activity.settings.primary_result': 'You made {resultType} the result your account leads with',
    'activity.settings.primary_result_auto':
      'You let your account pick its own headline result again',
    'activity.settings.several': 'You made {count} to your settings',

    // T-126 — the rest of API §7.1's kinds. A staging is not a change to the account yet, so its sentence says
    // "staged" and never "turned off"; the log has to be readable as a list of what actually happened.
    'activity.staged_turn_on': 'You staged turning on {entity:level} {entity}',
    'activity.staged_turn_off': 'You staged turning off {entity:level} {entity}',
    // D-193 (2) again — the staged twins say it the same way, or the log reads two ways about one fact.
    'activity.staged_budget_raised':
      "You staged raising {entity}'s daily budget {before:money.perDay} → {after:money.perDay}",
    'activity.staged_budget_lowered':
      "You staged lowering {entity}'s daily budget {before:money.perDay} → {after:money.perDay}",
    'activity.unstaged': 'You removed the staged change to {entity:level} {entity}',
    'activity.discarded': 'You discarded {count}',
    'activity.publish_failed': '{entity:level} {entity} could not be changed — nothing was sent',
    'activity.publish_refused': 'Publishing is off for this account, so {count} stayed staged',
    'activity.undone': 'You undid {count}',
    'activity.demo_reset': 'The demo was reset to the full story',
    'activity.unknown': 'Something changed on this account',

    // T-127 — the two View-only kinds (API §6.8). "Marked as done" is a claim about somewhere else, so its
    // sentence says what is and is not known yet; the verified one carries ARCH §7.3's exact phrase.
    'activity.marked_done':
      'You marked the change to {entity:level} {entity} as done — not yet seen on Meta',
    'activity.verified_outside': 'Done outside AdOrchestrator — verified: {entity:level} {entity}',

    // ---- the View-only checklist (API §6.9, REQ-104 AC5) --------------------------------------------------
    //
    // These lines are RETYPED BY A PERSON into Ads Manager, so every character is DEMO_STORY's and none of it
    // is assembled in code: the three subject keys spell their own level word because the line starts with it
    // and `{entity:level}` is the lower-case mid-sentence one ("ad set"), and even the " — " between the two
    // halves is `checklist.line`'s rather than a join in a renderer. Names only — no ids, no URLs, ever.
    'checklist.campaign': 'Campaign "{entity}"',
    'checklist.adset': 'Ad set "{entity}" (campaign "{parent}")',
    'checklist.ad': 'Ad "{entity}" (ad set "{parent}")',
    'checklist.turn_off': 'Turn off',
    'checklist.turn_on': 'Turn on',
    'checklist.daily_budget': 'Daily budget {from:money.perDay} → {to:money.perDay}',
    'checklist.lifetime_budget': 'Total budget {from:money.perDay} → {to:money.perDay}',
    'checklist.line': '{subject} — {change}',
    // REQ-006 AC4. It starts "Demo data" and never contains "Live", the same rule the chip follows (SC-6), so
    // a checklist pasted into an email cannot be mistaken for one from a real account.
    'checklist.watermark': 'Demo data — sample changes on a sample account. Nothing here is real.',

    // ---- mode (API §12.2 — spelled exactly) ---------------------------------------------------------------
    'mode.demo': 'Demo data',
    'mode.demo_view_only': 'Demo data · View-only preview',
    'mode.live_view_only': 'Live · View-only',
    'mode.live_editing': 'Live · Editing on',

    // ---- time (DESIGN_SYSTEM §2 rule 10) -------------------------------------------------------------------
    'time.just_now': 'Just now',
    'time.ago': '{n} ago',
    'time.yesterday': 'Yesterday',
    'time.on': '{date:date.day}',
    'time.duration': '{n}',
    'time.at': '{at}',
    'time.today_at': 'Today at {at}',
    'time.date_at': '{date:date.day}, {at}',

    // ---- window · level · metric words ------------------------------------------------------------------
    'window.today': 'today',
    'window.yesterday': 'yesterday',
    'window.last_3d': 'the last 3 days',
    'window.last_7d': 'the last 7 days',
    'window.last_14d': 'the last 14 days',
    'window.last_30d': 'the last 30 days',
    'level.campaign': 'campaign',
    'level.adset': 'ad set',
    'level.ad': 'ad',
    'ads_manager.sample_data': 'Sample data — there is nothing to open in Ads Manager',
    'metric.roas_sentence': '{back:money.exact} back per {one:money.perDay}',
    'metric.change_new': 'new',

    // ---- ribbon · preview (REQ-140 AC3, REQ-142 AC1, REQ-141 AC2/AC4 — API §8.1, §8.3) -------------------
    'ribbon.best_value': 'Best value',
    'ribbon.best_value.reason':
      'The lowest cost per {resultType:singular} among your running ads that aim for {resultType}',
    'ribbon.most_expensive': 'Most expensive',
    'ribbon.most_expensive.reason':
      'The highest cost per {resultType:singular} among your running ads that aim for {resultType}',
    'ribbon.worn_out': 'Worn out',
    'ribbon.worn_out.reason':
      'People have seen it often and are clicking it less — it may be costing you {resultType}',
    'preview.approximation':
      'This is an approximation: the frame is a neutral stand-in and the ad inside is the real one',
    // T-303 (D-561, REQ-141 AC2 v2.4): the frame holds the ad's OWN still — the note then says which part is ours.
    // T-169 (T-304 / T-305's ask): the product's apostrophe (’), as every other sentence here spells it.
    'preview.approximation.real_still':
      'Roughly how it looks. The picture and the words are the ad’s own; the frame is ours.',

    // ---- briefing (REQ-023, ENGINE §12 — T-164): one sentence, one number, the entity named ------------------
    'briefing.attention.no_results':
      '{entity} spent {spent:money.headline} in {window} with no {result:plurals}',
    'briefing.attention.cost_jumped':
      '{entity} is paying {pct:pct.share} more per {result:singular} than its recent level',
    'briefing.attention.overspend':
      'This month is heading for about {pct:pct.share} over your monthly plan',
    'briefing.attention.guardrail':
      'This month is heading past your {guardrail:money.headline} guardrail',
    'briefing.attention.rejected': 'Meta rejected an ad: {entity}',
    'briefing.attention.has_problem': "{entity} has a problem and isn't running properly",
    'briefing.attention.stopped_running': '{entity} stopped running — it spent nothing yesterday',
    'briefing.attention.account_problem': 'Your ad account has a problem, so nothing is running',
    'briefing.attention.wasted.no_results':
      '{entity} spent {spent:money.headline} in the last 7 days with no {result:plurals}',
    'briefing.attention.wasted.too_expensive':
      '{entity} pays {cost:money.cost} per {result:singular} — far more than others at its level',
    'briefing.attention.wasted.worn_out':
      '{entity} looks worn out — seen {seen:ratio.frequency} times on average',
    'briefing.attention.pacing.over_plan':
      'This month is {ahead:money.headline} ahead of your plan',
    'briefing.attention.pacing.under_plan':
      'This month is {behind:money.headline} behind your plan',
    'briefing.good.winner_limited':
      '{entity} gets {result:plurals} at {cost:money.cost} each, and its budget is holding it back',
    'briefing.good.cheaper':
      '{entity} is getting {result:plurals} {pct:pct.share} cheaper than its recent level',
    'briefing.good.best_day': '{date:date.day} was your best day in 30 days: {results}',
    'briefing.good.best_day_value':
      '{date:date.day} was your best day in 30 days: {value:money.cost} in value of results',
    'briefing.good.best_performer':
      '{entity} gets {result:plurals} for the least money: {cost:money.cost} each',
    'briefing.good.pacing.on_track':
      'This month is on track against your {plan:money.headline} plan',
    'briefing.empty.attention': 'Nothing needs your attention right now.',
    'briefing.empty.going_well': 'Nothing stands out yet — everything running is about average.',
    'briefing.empty.going_well_too_early':
      'Too early to tell — we need a few days of results before calling anything a winner.',

    // ---- first-run (REQ-005, ENGINE §12 — T-164): five cards, their thin-data variants, the last card ---------
    'first_run.spent':
      'You spent {spent:money.headline} in the last 30 days — about {perDay:money.headline} per day',
    'first_run.results': 'That brought in {results}, at about {cost:money.cost} each',
    'first_run.results.none': 'No {result:plurals} yet in the last 30 days',
    'first_run.results.unavailable':
      "Meta hasn't sent how many {result:plurals} the last 30 days brought",
    // D-479 (T-290): the best value is 7-day beside the 30-day totals of cards 1–2, so the sentence names its window
    'first_run.best_performer':
      '{entity} gets {result:plurals} at {cost:money.cost} each over the last 7 days — your best value',
    'first_run.best_performer.none':
      'No clear best value yet — nothing has enough results to compare',
    'first_run.concern.none': 'Nothing worrying right now — nothing is spending without results',
    'first_run.pacing.over':
      'This month is heading for about {forecast:money.headline} — {pct:pct.share} over your {plan:money.headline} plan',
    'first_run.pacing.under':
      'This month is heading for about {forecast:money.headline} — {pct:pct.share} under your {plan:money.headline} plan',
    'first_run.pacing.on_track':
      'This month is heading for about {forecast:money.headline} — on track against your {plan:money.headline} plan',
    'first_run.pacing.no_forecast':
      "You've spent {spent:money.headline} so far this month against a {plan:money.headline} plan — not enough history to forecast the rest yet",
    'first_run.pacing.no_plan':
      "You've spent {spent:money.headline} so far this month. There's no monthly plan yet, so nothing tells you whether that's more than you meant to spend",
    'first_run.not_enough_history': "Not enough history yet — here's what we'll show you",
    'first_run.spent.not_enough_history':
      'Your total for the last 30 days, and what that is per day',
    'first_run.results.not_enough_history':
      "Your results, named after your campaigns' goal, and what each one cost",
    'first_run.best_performer.not_enough_history':
      'The campaign, ad set or ad that gets results for the least money',
    'first_run.concern.not_enough_history':
      'Anything spending with nothing to show — or "nothing worrying right now"',
    'first_run.pacing.not_enough_history': 'Where the month is heading, against the plan you set',
    'first_run.alerts.enable': 'Switch on 3 recommended alerts',
    'first_run.alerts.enable.plan':
      "…and we'll use {plan:money.headline} as your monthly plan — change it any time",
    'first_run.alerts.already_watching':
      'Your recommended alerts are already watching this account',

    // REQ-025 — the three sentences the spend chart's own colour settings carry a FIGURE in (AC10 / D-158).
    // Everything else in that panel is fixed words and lives in each app's own copy table, byte-identical
    // under SC-2. The figures arrive pre-formatted in the CHOSEN METRIC's context (`colorByFormatContext`),
    // which is the one thing a single template cannot pin: the same slot is $8.91 for a cost, 1.39% for a
    // rate and $4.15 back per $1 for return on ad spend.
    'chart.colors.suggestion': 'The last 30 days ran {low} – {high} a purchase',
    'chart.colors.average': 'Average {value}',
    'chart.colors.no_value': 'No {metric} on this day — nothing was bought.',
  },
};
